import { RESTRICTED_KEYS } from 'shared/fixtures';
import { config } from 'config';
import i18n from 'i18nextInstance';
import intersection from 'lodash/intersection';
import {
    AbsenceReportList,
    AbsenceReportView,
    AgendaConfiguration,
    AgendaRegistration,
    AppointmentTypeListing,
    AppointmentTypeRegistration,
    AreaListing,
    CampaignListing,
    CampaignRegistration,
    CompanyListing,
    ConfigScreen,
    ControlListing,
    CostCenterListing,
    CoverageListing,
    CoverageRegistration,
    Dashboard,
    Error400,
    Error404,
    ErrorVersion,
    Login,
    LoginAccess,
    NotificationConfiguration,
    OfficeListing,
    OfficeRegistration,
    PatientAudit,
    PatientFiles,
    PatientHistory,
    PatientListing,
    PatientRecap,
    PatientRegistration,
    PinRegistration,
    PlatformConfiguration,
    PolyOffices,
    PositionListing,
    ProfessionalListing,
    ProfessionalRegistration,
    ResetPassword,
    RiskAgentListing,
    RiskAgentRegistration,
    Specialties,
    StatisticsAbsenteeism,
    StatisticsDiagnosis,
    StatisticsGeneral,
    StatisticsLicenses,
    StatisticsPatients,
    StatisticsPreworks,
    StatisticsProfessionals,
    StatisticsRecipes,
    StatisticsRecommendations,
    StatisticsSessions,
    StatisticsSurveys,
    StatisticsSymptoms,
    StatisticsTurns,
    StatisticsVideocall,
    TurnListing,
    UserListing,
    UserRegistration,
    VideoconferenceList,
    VideoconferenceRegistration,
    WorkplaceListing
} from 'views/containers';
import {
    getHideStatistics,
    isSuperadmin
} from 'shared/utils';
import {
    userIsAuthenticated,
    userIsNotAuthenticated,
    versionChecked
} from './auth.config';
const {
    absenceReport,
    allowGlobalCallRates,
    appointmentTypes,
    areas,
    auditPatient,
    campaigns,
    company,
    controls,
    costCenters,
    coverages,
    entity,
    files,
    offices,
    patientListing,
    prework,
    polyOffices,
    positions,
    recipesFiles,
    recipesRequests,
    recording,
    restrictedMenu,
    riskAgents,
    ssoLogin,
    turns,
    videoconference,
    workplaces
} = config;

const {
    absenteeism,
    diagnostics,
    licenses,
    general,
    patients,
    preworks,
    professionals,
    recipes,
    recommendations,
    sessions,
    statistics,
    surveys,
    symptoms,
    turns: statisticsTurns,
    videocall
} = getHideStatistics();

export const isRouteAvailable = (routeObject, user) => {
    if (!routeObject) return false;
    const {
        blocked,
        onlySuperadmin = false,
        permissions = [],
        restricted,
        restrictedKey
    } = routeObject;
    const isNotRestricted = (!restricted && !restrictedMenu.includes(restrictedKey)) || !user.restricted_access;
    const isAllowed = !permissions || !user.locked_actions || intersection(user.locked_actions, permissions).length === 0;
    const isSuperadminLocked = onlySuperadmin && !isSuperadmin(user?.email);
    const isAvailable = !blocked && isNotRestricted && isAllowed && !isSuperadminLocked;
    return isAvailable;
};

const findRouteObjectByPath = path => {
    const matchUrl = url => {
        const regexPattern = `^${ path.replace(/:\w+/g, '[^/]+') }$`;
        const regex = new RegExp(regexPattern);
        return regex.test(url);
    };
    const url = path.split('?')[0];
    return privateRoutes.find(({ path }) => matchUrl(Array.isArray(path) ? path[0] : path, url));
};

export const isPathAvailable = (path, user) => isRouteAvailable(findRouteObjectByPath(path), user);

export const publicRoutes = [{
    blocked: !ssoLogin,
    component: versionChecked(LoginAccess),
    path: [i18n.t('/acceso'), '/access']
}, {
    component: versionChecked(userIsNotAuthenticated(Login)),
    path: i18n.t('/login')
}, {
    component: ErrorVersion,
    path: [i18n.t('/nueva-version'), '/new-version']
}, {
    component: versionChecked(Error400),
    path: i18n.t('/error400')
}, {
    component: versionChecked(Error404),
    path: i18n.t('/error404')
}, {
    component: versionChecked(userIsNotAuthenticated(ResetPassword)),
    path: [i18n.t('/restablecer'), '/restore']
}];

export const privateRoutes = [{
    blocked: !turns,
    component: versionChecked(userIsAuthenticated(AgendaConfiguration)),
    path: [i18n.t('/agenda'), '/schedule'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointments
}, {
    blocked: !turns,
    component: versionChecked(userIsAuthenticated(AgendaRegistration)),
    path: [i18n.t('/agenda/crear'), '/agenda/create'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointments
}, {
    blocked: !turns,
    component: versionChecked(userIsAuthenticated(AgendaRegistration)),
    path: [i18n.t('/agenda/editar/:id'), '/agenda/edit/:id'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointments
}, {
    blocked: !riskAgents,
    component: versionChecked(userIsAuthenticated(RiskAgentListing)),
    path: '/agentes',
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !riskAgents,
    component: versionChecked(userIsAuthenticated(RiskAgentRegistration)),
    path: '/agentes/crear',
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !riskAgents,
    component: versionChecked(userIsAuthenticated(RiskAgentRegistration)),
    path: '/agentes/:id',
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !areas,
    component: versionChecked(userIsAuthenticated(AreaListing)),
    path: [i18n.t('/zonas'), '/areas'],
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !absenceReport,
    component: versionChecked(userIsAuthenticated(AbsenceReportList)),
    path: '/ausencias',
    restrictedKey: RESTRICTED_KEYS.absences
}, {
    blocked: true || !absenceReport,
    component: versionChecked(userIsAuthenticated(AbsenceReportView)),
    path: '/ausencias/:id',
    restrictedKey: RESTRICTED_KEYS.absences
}, {
    blocked: !positions,
    component: versionChecked(userIsAuthenticated(PositionListing)),
    path: [i18n.t('/cargos'), '/positions'],
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !campaigns,
    component: versionChecked(userIsAuthenticated(CampaignListing)),
    path: [i18n.t('/campanas'), '/campaigns'],
    restrictedKey: RESTRICTED_KEYS.campaigns
}, {
    blocked: !campaigns,
    component: versionChecked(userIsAuthenticated(CampaignRegistration)),
    path: [i18n.t('/campanas/crear'), '/campaigns/create'],
    restrictedKey: RESTRICTED_KEYS.campaigns
}, {
    blocked: !campaigns,
    component: versionChecked(userIsAuthenticated(CampaignRegistration)),
    path: [i18n.t('/campanas/clonar/:id'), '/campaigns/clone/:id'],
    restrictedKey: RESTRICTED_KEYS.campaigns
}, {
    blocked: !campaigns,
    component: versionChecked(userIsAuthenticated(CampaignRegistration)),
    path: [i18n.t('/campanas/editar/:id'), '/campaigns/edit/:id'],
    restrictedKey: RESTRICTED_KEYS.campaigns
}, {
    blocked: !costCenters,
    component: versionChecked(userIsAuthenticated(CostCenterListing)),
    path: [i18n.t('/centros'), '/centers'],
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !coverages,
    component: versionChecked(userIsAuthenticated(CoverageRegistration)),
    path: [i18n.t('/coberturas/crear'), '/coverages/create'],
    restrictedKey: RESTRICTED_KEYS.coverages
}, {
    blocked: !coverages,
    component: versionChecked(userIsAuthenticated(CoverageRegistration)),
    path: [i18n.t('/coberturas/:mode/:id'), '/coverages/:mode/:id'],
    restrictedKey: RESTRICTED_KEYS.coverages
}, {
    blocked: !coverages,
    component: versionChecked(userIsAuthenticated(CoverageListing)),
    path: [i18n.t('/coberturas/listar'), '/coverages/list'],
    restrictedKey: RESTRICTED_KEYS.coverages
}, {
    blocked: !recording && !recipesFiles && !allowGlobalCallRates && !licenses,
    component: versionChecked(userIsAuthenticated(ConfigScreen)),
    path: [i18n.t('/configuracion'), '/configuration'],
    restrictedKey: RESTRICTED_KEYS.configurations
}, {
    blocked: !turns || !appointmentTypes,
    component: versionChecked(userIsAuthenticated(AppointmentTypeRegistration)),
    path: [i18n.t('/consultas/tipos/crear'), '/consultations/types/create'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointmentTypes
}, {
    blocked: !turns || !appointmentTypes,
    component: versionChecked(userIsAuthenticated(AppointmentTypeRegistration)),
    path: [i18n.t('/consultas/tipos/editar/:id'), '/consultations/types/edit/:id'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointmentTypes
}, {
    blocked: !turns || !appointmentTypes,
    component: versionChecked(userIsAuthenticated(AppointmentTypeListing)),
    path: [i18n.t('/consultas/tipos/listar'), '/consultations/types/list'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointmentTypes
}, {
    blocked: !turns || !appointmentTypes,
    component: versionChecked(userIsAuthenticated(AppointmentTypeRegistration)),
    path: [i18n.t('/consultas/tipos/ver/:id'), '/consultations/types/view/:id'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointmentTypes
}, {
    blocked: !offices,
    component: versionChecked(userIsAuthenticated(OfficeListing)),
    path: [i18n.t('/consultorios/listar'), '/offices/list'],
    restrictedKey: RESTRICTED_KEYS.offices
}, {
    blocked: !offices,
    component: versionChecked(userIsAuthenticated(OfficeRegistration)),
    path: [i18n.t('/consultorios/:mode'), '/offices/:mode'],
    restrictedKey: RESTRICTED_KEYS.offices
}, {
    blocked: !offices,
    component: versionChecked(userIsAuthenticated(OfficeRegistration)),
    path: [i18n.t('/consultorios/:mode/:id'), '/offices/:mode/:id'],
    restrictedKey: RESTRICTED_KEYS.offices
}, {
    blocked: !controls,
    component: versionChecked(userIsAuthenticated(ControlListing)),
    path: [i18n.t('/controles'), '/controls'],
    restrictedKey: RESTRICTED_KEYS.controls
}, {
    component: versionChecked(userIsAuthenticated(Dashboard)),
    path: i18n.t('/dashboard'),
    restrictedKey: RESTRICTED_KEYS.dashboard
}, {
    blocked: !company,
    component: versionChecked(userIsAuthenticated(CompanyListing)),
    path: [i18n.t('/empresas'), '/companies'],
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    component: versionChecked(userIsAuthenticated(Specialties)),
    path: [i18n.t('/especialidades'), '/specialties'],
    restrictedKey: RESTRICTED_KEYS.specialties
}, {
    blocked: statistics || absenteeism,
    component: versionChecked(userIsAuthenticated(StatisticsAbsenteeism)),
    path: [i18n.t('/estadisticas/ausentismo'), '/statistics/absenteeism'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || !prework || preworks,
    component: versionChecked(userIsAuthenticated(StatisticsPreworks)),
    path: [i18n.t('/estadisticas/controles-ocupacionales'), '/statistics/preworks'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || diagnostics,
    component: versionChecked(userIsAuthenticated(StatisticsDiagnosis)),
    path: [i18n.t('/estadisticas/diagnosticos'), '/statistics/diagnosis'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || general,
    component: versionChecked(userIsAuthenticated(StatisticsGeneral)),
    path: [i18n.t('/estadisticas/general'), '/statistics/general'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || licenses,
    component: versionChecked(userIsAuthenticated(StatisticsLicenses)),
    path: [i18n.t('/estadisticas/licencias'), '/statistics/licenses'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || surveys,
    component: versionChecked(userIsAuthenticated(StatisticsSurveys)),
    path: [i18n.t('/estadisticas/encuestas'), '/statistics/surveys'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || patients,
    component: versionChecked(userIsAuthenticated(StatisticsPatients)),
    path: [i18n.t('/estadisticas/pacientes'), '/statistics/patients'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || professionals,
    component: versionChecked(userIsAuthenticated(StatisticsProfessionals)),
    path: [i18n.t('/estadisticas/profesionales'), '/statistics/professionals'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: !recipesRequests || statistics || recipes,
    component: versionChecked(userIsAuthenticated(StatisticsRecipes)),
    path: [i18n.t('/estadisticas/recetas'), '/statistics/recipes'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || recommendations,
    component: versionChecked(userIsAuthenticated(StatisticsRecommendations)),
    path: [i18n.t('/estadisticas/recomendaciones'), '/statistics/recommendations'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || sessions,
    component: versionChecked(userIsAuthenticated(StatisticsSessions)),
    path: [i18n.t('/estadisticas/sesiones'), '/statistics/sessions'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || symptoms,
    component: versionChecked(userIsAuthenticated(StatisticsSymptoms)),
    path: [i18n.t('/estadisticas/sintomas'), '/statistics/symptoms'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || statisticsTurns,
    component: versionChecked(userIsAuthenticated(StatisticsTurns)),
    path: [i18n.t('/estadisticas/turnos'), '/statistics/turns'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: statistics || videocall,
    component: versionChecked(userIsAuthenticated(StatisticsVideocall)),
    path: [i18n.t('/estadisticas/videollamada'), '/statistics/videocall'],
    permissions: ['view_statistics'],
    restrictedKey: RESTRICTED_KEYS.statistics
}, {
    blocked: !workplaces,
    component: versionChecked(userIsAuthenticated(WorkplaceListing)),
    path: [i18n.t('/lugares'), '/workplaces'],
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    blocked: !entity,
    component: versionChecked(userIsAuthenticated(PinRegistration)),
    path: [i18n.t('/pin/crear'), '/pin/create']
}, {
    blocked: !files,
    component: versionChecked(userIsAuthenticated(PatientFiles)),
    path: [i18n.t('/pacientes/archivos/:id'), '/patients/files/:id'],
    permissions: ['patients_files'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    blocked: !patientListing || !auditPatient,
    component: versionChecked(userIsAuthenticated(PatientAudit)),
    path: [i18n.t('/pacientes/auditoria/:id'), '/patients/audit/:id'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    blocked: !patientListing,
    component: versionChecked(userIsAuthenticated(PatientRegistration)),
    path: [i18n.t('/pacientes/editar/:id'), '/patients/edit/:id'],
    permissions: ['patients_edit'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    component: versionChecked(userIsAuthenticated(PatientRegistration)),
    path: [i18n.t('/pacientes/crear'), '/patients/create'],
    permissions: ['patients_create'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    component: versionChecked(userIsAuthenticated(PatientRecap)),
    path: [i18n.t('/pacientes/evolucion/:id'), '/patients/evolution/:id'],
    permissions: ['call_data', 'call_history'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    blocked: !patientListing,
    component: versionChecked(userIsAuthenticated(PatientHistory)),
    path: [i18n.t('/pacientes/historial/:id'), '/patients/history/:id'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    blocked: !patientListing,
    component: versionChecked(userIsAuthenticated(PatientListing)),
    path: [i18n.t('/pacientes/listar'), '/patients/list'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    component: versionChecked(userIsAuthenticated(PatientRecap)),
    path: [i18n.t('/pacientes/registro/:id'), '/patients/recap/:id'],
    permissions: ['call_data', 'call_history'],
    restrictedKey: RESTRICTED_KEYS.patients
}, {
    component: versionChecked(userIsAuthenticated(UserRegistration)),
    path: [i18n.t('/perfil'), '/profile']
}, {
    blocked: !polyOffices,
    component: versionChecked(userIsAuthenticated(PolyOffices)),
    path: [i18n.t('/policonsultorios'), '/polyOffices'],
    restrictedKey: RESTRICTED_KEYS.manage
}, {
    component: versionChecked(userIsAuthenticated(ProfessionalRegistration)),
    path: [i18n.t('/profesionales/crear'), '/professionals/create'],
    permissions: ['professionals_create'],
    restrictedKey: RESTRICTED_KEYS.professionals
}, {
    component: versionChecked(userIsAuthenticated(ProfessionalRegistration)),
    path: [i18n.t('/profesionales/editar/:id'), '/professionals/editar/:id'],
    permissions: ['professionals_edit'],
    restrictedKey: RESTRICTED_KEYS.professionals
}, {
    component: versionChecked(userIsAuthenticated(ProfessionalListing)),
    path: [i18n.t('/profesionales/listar'), '/professionals/list'],
    restrictedKey: RESTRICTED_KEYS.professionals
}, {
    component: versionChecked(userIsAuthenticated(ProfessionalRegistration)),
    path: [i18n.t('/profesionales/ver/:id'), '/professionals/view/:id'],
    restrictedKey: RESTRICTED_KEYS.professionals
}, {
    component: versionChecked(userIsAuthenticated(NotificationConfiguration)),
    onlySuperadmin: true,
    path: [i18n.t('/sys/notificaciones'), '/sys/notifications']
}, {
    component: versionChecked(userIsAuthenticated(PlatformConfiguration)),
    onlySuperadmin: true,
    path: [i18n.t('/sys/configuracion'), '/sys/configuration']
}, {
    component: versionChecked(userIsAuthenticated(TurnListing)),
    path: [i18n.t('/turnos'), '/appointments'],
    permissions: ['appointments'],
    restrictedKey: RESTRICTED_KEYS.appointments
}, {
    component: versionChecked(userIsAuthenticated(UserRegistration)),
    path: [i18n.t('/usuarios/crear'), '/users/create'],
    restricted: true
}, {
    component: versionChecked(userIsAuthenticated(UserRegistration)),
    path: [i18n.t('/usuarios/editar/:id'), '/users/edit/:id'],
    restricted: true
}, {
    component: versionChecked(userIsAuthenticated(UserListing)),
    path: [i18n.t('/usuarios/listar'), '/users/list'],
    restricted: true
}, {
    component: versionChecked(userIsAuthenticated(UserRegistration)),
    path: [i18n.t('/usuarios/ver/:id'), '/users/view/:id'],
    restricted: true
}, {
    blocked: !videoconference,
    component: versionChecked(userIsAuthenticated(VideoconferenceRegistration)),
    path: [i18n.t('/videoconferencia/crear'), '/videoconference/create']
}, {
    blocked: !videoconference,
    component: versionChecked(userIsAuthenticated(VideoconferenceRegistration)),
    path: [i18n.t('/videoconferencia/editar/:id'), '/videoconference/edit/:id']
}, {
    blocked: !videoconference,
    component: versionChecked(userIsAuthenticated(VideoconferenceList)),
    path: [i18n.t('/videoconferencia/listar'), '/videoconference/list']
}, {
    blocked: !videoconference,
    component: versionChecked(userIsAuthenticated(VideoconferenceRegistration)),
    path: [i18n.t('/videoconferencia/ver/:id'), '/videoconference/view/:id']
}];
